import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ArrowLeft, Pencil, Trash2, Search, Globe, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { pickLocalized, type Localized } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';

interface PostSummary {
  id: string;
  slug: string;
  locale: string;
  title: Localized;
  excerpt: Localized;
  status: 'draft' | 'published' | 'archived';
  updated_at: string;
}

interface Paginated<T> {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
}

export const Route = createFileRoute('/_app/post-types/$key/posts/')({
  component: PostsListPage,
});

function PostsListPage() {
  const { key } = Route.useParams();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | 'draft' | 'published'>('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['posts', key, page, status],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: '25', page: String(page) });
      if (status) params.set('status', status);
      return unwrap<Paginated<PostSummary>>(
        api.get(`/post-types/${key}/posts?${params}`),
      );
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/post-types/${key}/posts/${id}`),
    onSuccess: () => {
      toast.success('Eliminado');
      void qc.invalidateQueries({ queryKey: ['posts', key] });
    },
  });

  const filtered = !q
    ? data?.data
    : data?.data.filter((p) => {
        const t = pickLocalized(p.title, p.locale, p.slug);
        const e = pickLocalized(p.excerpt, p.locale, '');
        return (t + e + p.slug).toLowerCase().includes(q.toLowerCase());
      });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <Link to="/post-types" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900">
        <ArrowLeft className="size-3" /> Tipos de contenido
      </Link>

      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight capitalize">{key}</h1>
          <p className="text-sm text-neutral-500">{data?.total ?? 0} entradas</p>
        </div>
        <Button asChild>
          <Link to="/post-types/$key/posts/$id" params={{ key, id: 'new' }}>
            <Plus className="size-4" /> Nueva entrada
          </Link>
        </Button>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Listado</CardTitle>
              <CardDescription>Borradores y publicados.</CardDescription>
            </div>
            <div className="flex gap-2">
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value as ''); setPage(1); }}
                className="h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm"
              >
                <option value="">Todos</option>
                <option value="draft">Borradores</option>
                <option value="published">Publicados</option>
              </select>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="pl-9" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-2">Título</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Actualizada</th>
                <th className="px-4 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Cargando…</td></tr>
              )}
              {filtered?.length === 0 && !isLoading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Sin entradas.</td></tr>
              )}
              {filtered?.map((post) => {
                const title = pickLocalized(post.title, post.locale, post.slug);
                return (
                  <tr key={post.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium">{title}</td>
                    <td className="px-4 py-3 text-neutral-500">/{post.slug}</td>
                    <td className="px-4 py-3"><StatusBadge status={post.status} /></td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {new Date(post.updated_at).toLocaleDateString('es')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" asChild>
                        <Link to="/post-types/$key/posts/$id" params={{ key, id: post.id }}>
                          <Pencil className="size-3.5" />
                        </Link>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { if (confirm(`¿Eliminar "${title}"?`)) remove.mutate(post.id); }}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data && data.last_page > 1 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-xs text-neutral-500">
              <span>Página {data.current_page} de {data.last_page}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: 'draft' | 'published' | 'archived' }) {
  if (status === 'published') return <Badge variant="success"><Globe className="mr-1 size-3" /> Publicada</Badge>;
  if (status === 'archived') return <Badge variant="warning"><EyeOff className="mr-1 size-3" /> Archivada</Badge>;
  return <Badge variant="secondary">Borrador</Badge>;
}
